import { create } from 'zustand';
import { useCallback } from 'react';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface ToastDescriptor {
  id: string;
  message: string;
  tone: ToastTone;
  durationMs: number;
}

interface ToastState {
  toasts: ToastDescriptor[];
  pushToast: (toast: Omit<ToastDescriptor, 'id'> & { id?: string }) => string;
  removeToast: (id: string) => void;
  clearAll: () => void;
}

const generateId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  pushToast: (toast) => {
    const id = toast.id ?? generateId();
    const descriptor: ToastDescriptor = {
      id,
      tone: toast.tone,
      message: toast.message,
      durationMs: toast.durationMs
    };
    set((state) => ({
      toasts: [...state.toasts.filter((item) => item.id !== id), descriptor]
    }));
    return id;
  },
  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.id !== id)
    })),
  clearAll: () => set({ toasts: [] })
}));

export interface ShowToastOptions {
  message: string;
  tone?: ToastTone;
  durationMs?: number;
}

const DEFAULT_DURATION = 6000;

function scheduleRemoval(id: string, durationMs: number) {
  if (typeof window !== 'undefined' && Number.isFinite(durationMs) && durationMs > 0) {
    window.setTimeout(() => {
      const removeToast = useToastStore.getState().removeToast;
      removeToast(id);
    }, durationMs);
  }
}

export function useToast() {
  const pushToast = useToastStore((state) => state.pushToast);

  return useCallback(
    ({ message, tone = 'info', durationMs = DEFAULT_DURATION }: ShowToastOptions) => {
      const id = pushToast({ message, tone, durationMs });
      scheduleRemoval(id, durationMs);
      return id;
    },
    [pushToast]
  );
}

export function showToast({
  message,
  tone = 'info',
  durationMs = DEFAULT_DURATION
}: ShowToastOptions) {
  const { pushToast } = useToastStore.getState();
  const id = pushToast({ message, tone, durationMs });
  scheduleRemoval(id, durationMs);
  return id;
}
